import { StepComponent } from '@/components/StepNavigation';
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton';
import { authLinkClassName } from '@/components/auth/authStyles';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AccountType } from '@paladin-monorepo/common';
import { Building2, Check, LucideProps, Users } from 'lucide-react';
import { RefAttributes } from 'react';
import { LinkedInContinueButton } from '@/components/auth/LinkedInContinueButton';
import { GoogleContinueButton } from '@/components/auth/GoogleContinueButton';
import { AuthDivider } from '@/components/auth/AuthDivider';
import { useSearchParams, useNavigate } from 'react-router';
import { SignupState } from '../signup';
import { useTranslation } from 'react-i18next';

const TypeCard = ({
  type,
  title,
  desc,
  state,
  setState,
  icon: Icon,
}: {
  type: AccountType;
  title: string;
  desc: string;
  state?: SignupState;
  setState: (state: (state: SignupState) => SignupState) => void;
  icon: React.ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>
  >;
}) => (
  <button
    onClick={() =>
      setState((state: SignupState) => ({ ...state, accountType: type }))
    }
    className={cn(
      'group relative flex h-full flex-col gap-4 rounded-lg border-2 p-6 text-left transition-all duration-200',
      state?.accountType === type
        ? 'border-brand-primary bg-brand-primary/5 shadow-sm'
        : 'border-border-subtle bg-white hover:border-brand-secondary-bright/40 dark:bg-card',
    )}
  >
    <div
      className={cn(
        'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
        state?.accountType === type
          ? 'bg-brand-primary text-white'
          : 'bg-brand-secondary-bright/10 text-brand-secondary group-hover:bg-brand-primary/10 group-hover:text-brand-primary',
      )}
    >
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h3
        className={cn(
          'mb-1 font-semibold',
          state?.accountType === type ? 'text-brand-primary' : 'text-brand-secondary',
        )}
      >
        {title}
      </h3>
      <p className="text-sm leading-relaxed text-brand-secondary/70">{desc}</p>
    </div>
    {state?.accountType === type && (
      <div className="absolute top-4 right-4 text-brand-primary">
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white">
          <Check className="w-3 h-3" />
        </div>
      </div>
    )}
  </button>
);

export const Step01AccountType: React.ComponentType<StepComponent> = ({
  onContinue,
  state,
  setState,
}: {
  onContinue: () => void;
  state?: SignupState;
  setState: (state: (state: SignupState) => SignupState) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get('ref');
  const isSsoReturn = refParam === 'linkedin' || refParam === 'google';

  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 gap-6">
        <TypeCard
          type={AccountType.Inquirer}
          title={t('signup.step01.cards.inquirer.title')}
          desc={t('signup.step01.cards.inquirer.description')}
          icon={Building2}
          state={state}
          setState={setState}
        />
        <TypeCard
          type={AccountType.Consultant}
          title={t('signup.step01.cards.consultant.title')}
          desc={t('signup.step01.cards.consultant.description')}
          icon={Users}
          state={state}
          setState={setState}
        />
      </div>
      {!isSsoReturn && (
        <div className="mx-auto flex w-full max-w-xs flex-col gap-4">
          <AuthDivider />
          <LinkedInContinueButton />
          <GoogleContinueButton />
        </div>
      )}
      <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
        <Button
          variant="ghost"
          className={authLinkClassName}
          onClick={() => {
            navigate('/auth/login');
          }}
        >
          {t('signup.login.backToLogin')}
        </Button>
        <AuthSubmitButton
          disabled={!state?.accountType}
          onClick={onContinue}
          className="w-full max-w-xs sm:w-auto"
        >
          {t('generic.continue')}
        </AuthSubmitButton>
      </div>
    </div>
  );
};
